import AdminPage from "admin/admin_page";
import { unwrapOrThrow } from "admin/api/api_result";
import { createScript, getScript, getTeamManagerOrAdminUsers, updateScript } from "admin/rest_api";
import { Button, Col, Form, Input, Row, Select, theme } from "antd";
import { useEffectOnlyOnce, useWkSelector } from "libs/react_hooks";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { APIUser } from "types/api_types";
import { enforceActiveUser } from "viewer/model/accessors/user_accessor";

const FormItem = Form.Item;

function ScriptCreateView() {
  const { scriptId } = useParams();
  const { token } = theme.useToken();

  const navigate = useNavigate();
  const activeUser = useWkSelector((state) => enforceActiveUser(state.activeUser));
  const [users, setUsers] = useState<APIUser[]>([]);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [form] = Form.useForm();

  async function fetchData() {
    setIsFetchingData(true);
    const fetchedUsers = unwrapOrThrow(await getTeamManagerOrAdminUsers());
    const onlyActiveUsers = fetchedUsers.filter((user) => user.isActive);
    setUsers(onlyActiveUsers);
    setIsFetchingData(false);
  }

  async function applyDefaults() {
    const script = scriptId ? await getScript(scriptId) : null;
    const defaultValues = {
      owner: script ? script.owner.id : activeUser.id,
    };
    const defaultFormValues = Object.assign({}, script, defaultValues);
    form.setFieldsValue(defaultFormValues);
  }

  useEffectOnlyOnce(() => {
    fetchData();
    applyDefaults();
  });

  // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'formValues' implicitly has an 'any' typ... Remove this comment to see the full error message
  const onFinish = async (formValues) => {
    if (scriptId) {
      await updateScript(scriptId, formValues);
    } else {
      await createScript(formValues);
    }

    navigate("/scripts");
  };

  const titlePrefix = scriptId ? "Update" : "Create";
  return (
    <AdminPage
      title={`${titlePrefix} Script`}
      descriptionURI="https://docs.webknossos.org/webknossos/tasks_projects/scripts.html"
      description="Create or update reusable frontend scripts and assign ownership."
      contentMaxWidth={800}
    >
      <div
        style={{
          padding: token.paddingLG,
        }}
      >
        <Form onFinish={onFinish} layout="vertical" form={form}>
          <Row gutter={token.paddingMD}>
            <Col xs={24} md={12}>
              <FormItem
                name="name"
                label="Script Name"
                hasFeedback
                rules={[
                  {
                    required: true,
                  },
                  {
                    min: 3,
                  },
                ]}
              >
                <Input autoFocus />
              </FormItem>
            </Col>
            <Col xs={24} md={12}>
              <FormItem
                name="owner"
                label="Owner"
                hasFeedback
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  showSearch={{ optionFilterProp: "label" }}
                  placeholder="Select a User"
                  style={{
                    width: "100%",
                  }}
                  loading={isFetchingData}
                  options={users.map((user: APIUser) => ({
                    value: user.id,
                    label: `${user.lastName}, ${user.firstName} (${user.email})`,
                  }))}
                />
              </FormItem>
            </Col>
          </Row>

          <FormItem
            name="gist"
            label="Gist URL"
            hasFeedback
            rules={[
              {
                required: true,
              },
              {
                type: "url",
              },
            ]}
          >
            <Input />
          </FormItem>

          <FormItem>
            <Button type="primary" htmlType="submit">
              {titlePrefix} Script
            </Button>
          </FormItem>
        </Form>
      </div>
    </AdminPage>
  );
}

export default ScriptCreateView;
